import React from 'react';
import { User, UserRole } from '../../../types/user';
import { LeavePolicyService } from '../../../services/leavePolicyService';

/**
 * Fallback display metadata for the codes that ship by default.
 *
 * These are a *last resort* only. Leave types are user-editable — a code can be
 * renamed EL → AL, and new types can be created — so anything user-facing must read
 * the live master list via leaveTypeName / leaveTypeColor below. Consulting these maps
 * directly is what left renamed codes showing their old label on some pages.
 */
const FALLBACK_NAMES: Record<string, string> = {
  EL: 'Earned Leave',
  CL: 'Casual Leave',
  SL: 'Sick Leave',
  ML: 'Maternity Leave',
  PL: 'Paternity Leave',
  CO: 'Comp-Off',
};

const FALLBACK_COLORS: Record<string, string> = {
  EL: '#3b82f6',
  CL: '#10b981',
  SL: '#ef4444',
  ML: '#8b5cf6',
  PL: '#6366f1',
  CO: '#f59e0b',
};

/** Live name for a leave code, resolved from the configured master list. */
export const leaveTypeName = (code: string): string => {
  const key = (code || '').toUpperCase();
  const configured = LeavePolicyService.getLeaveTypes()
    .find(t => t.code.toUpperCase() === key);
  return configured?.name || FALLBACK_NAMES[key] || code;
};

/** Live colour for a leave code, resolved from the configured master list. */
export const leaveTypeColor = (code: string): string => {
  const key = (code || '').toUpperCase();
  const configured = LeavePolicyService.getLeaveTypes()
    .find(t => t.code.toUpperCase() === key);
  return configured?.color || FALLBACK_COLORS[key] || '#0d9488';
};

/**
 * Proxy objects so existing `LEAVE_NAMES[code]` / `LEAVE_COLORS[code]` call sites pick
 * up renames without each one having to change. Reads hit the live master list.
 */
export const LEAVE_NAMES: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_t, code: string) => leaveTypeName(String(code)),
  has: () => true,
});

export const LEAVE_COLORS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_t, code: string) => leaveTypeColor(String(code)),
  has: () => true,
});

/**
 * Leave is only ever planned forward, so the pickers offer the current year and
 * the next `forward` years — never a past one. Computed on call rather than
 * frozen at module load: the old hardcoded [2024…2027] list both exposed years
 * nobody could still book into and would have silently stopped offering "next
 * year" once 2027 arrived.
 */
export const leaveYears = (forward = 2): number[] => {
  const current = new Date().getFullYear();
  return Array.from({ length: forward + 1 }, (_, i) => current + i);
};

/** Default window (current + next two), for callers that render their own <select>. */
export const LEAVE_YEARS = leaveYears();

/**
 * A stored ISO date as people read it: 2026-08-01 → 01 Aug 2026.
 *
 * Leave data is stored ISO because that sorts and compares correctly, but ISO is not how a
 * date is read — the Leave Assignment sheet showed raw 2026-08-01 in its Date of Joining
 * column and in the exit chip. Parsed from the date parts rather than handed to `new Date()`
 * on the bare string, because that parses as UTC midnight and can render as the previous day
 * for anyone west of UTC.
 *
 * Returns the input unchanged when it is not a plain ISO date, so a malformed or already
 * formatted value is passed through rather than becoming "Invalid Date".
 */
export const formatLeaveDate = (iso?: string | null): string => {
  const value = (iso || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Anchor id of the "My Leave History" table on Apply Leave.
 *
 * Shared rather than a literal at each end, so the link and the scroll target cannot drift
 * apart into a link that silently does nothing. Apply Leave opens on the month calendar, so
 * anything sending someone there to read their own request list has to name this.
 */
export const LEAVE_HISTORY_ANCHOR = 'my-leave-history';

/**
 * How healthy a leave balance is, as three levels the CSS can colour by.
 *
 * One definition for the whole module, because "is this balance running out" must not be
 * answered differently on two screens showing the same number. The 34% threshold and the
 * `balance <= 0` case are the values Leave Policies has always used; this is that function,
 * moved here so the Leave Dashboard's usage meter can share it rather than copy it.
 *
 * `granted` is the entitlement the balance is measured against — pass
 * LeaveBalanceService.totalEntitlement(snapshot), not the bare `granted` field, or a Comp-Off
 * balance (earned, never granted) divides by zero and always reads 'ok'.
 */
export const balanceLevel = (balance: number, granted: number): 'none' | 'low' | 'ok' => {
  if (balance <= 0) return 'none';
  if (granted <= 0) return 'ok';
  return balance / granted < 0.34 ? 'low' : 'ok';
};

// Role helpers — matches the sidebar visibility table exactly.
export const isManagerRole = (user: User | null | undefined): boolean =>
  !!user && (user.role === UserRole.MANAGER || user.role === UserRole.HR_ADMIN || user.role === UserRole.SUPER_ADMIN);

export const isHrRole = (user: User | null | undefined): boolean =>
  !!user && (user.role === UserRole.HR_ADMIN || user.role === UserRole.SUPER_ADMIN);

interface LeavePageHeaderProps {
  eyebrow?: string;
  title: string;
  actions?: React.ReactNode;
}

// Shared page header, reusing the module's existing `.leave-header` styling.
export const LeavePageHeader: React.FC<LeavePageHeaderProps> = ({
  eyebrow,
  title,
  actions,
}) => (
  <div className="leave-header">
    <div>
      {eyebrow && <p className="leave-eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
    </div>
    {actions && <div className="leave-header-actions">{actions}</div>}
  </div>
);

interface YearSelectProps {
  value: number;
  onChange: (year: number) => void;
  /** How many years past the current one to offer. Defaults to 2 (current + next two);
   *  the Team Matrix passes 1 since it only plans one year ahead. */
  forward?: number;
}

export const YearSelect: React.FC<YearSelectProps> = ({ value, onChange, forward = 2 }) => {
  // A stored year can fall outside the offered window (a saved filter, or a
  // page left open across New Year). Keep it listed so the select never renders
  // blank and silently misreports which year is being shown.
  const years = React.useMemo(() => {
    const base = leaveYears(forward);
    return base.includes(value) ? base : [...base, value].sort((a, b) => a - b);
  }, [forward, value]);

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="leave-year-select"
      aria-label="Select year"
    >
      {years.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  );
};

/**
 * Who a request is currently waiting on, for the states where it is waiting on someone.
 *
 * "Submitted" tells an employee that their request left their hands and nothing else — not who
 * has it, not who to chase. The approver is their reporting manager, so the answer already
 * exists; it was simply never shown on the employee's own list.
 *
 * The manager is resolved by the caller from User Management rather than read off the leave
 * record, because a Leave row does not carry one: the GraphQL `Leave` type has no managerEmail
 * field, and LeaveApprovalsPage already resolves the applicant's manager from the directory for
 * its notifications. Same source, so the two cannot name different people, and no schema change.
 *
 * Falls back to the email when the directory has no display name, and to a role-level statement
 * when there is no manager on file at all — an employee with no reporting line set still has
 * their request picked up by HR, and "Pending approval" with no name is more honest than naming
 * someone who is not their manager.
 */
export const pendingApprovalLabel = (
  status: string,
  manager?: { name?: string; email?: string }
): string | undefined => {
  const value = String(status || '').toLowerCase();
  // The two states where somebody genuinely owes a decision. Approved, Rejected and Cancelled
  // are settled, and labelling them "pending" would contradict the badge beside it.
  const awaitingDecision = value === 'submitted' || value === 'pending'
    || value === 'cancellation requested';
  if (!awaitingDecision) return undefined;

  const who = (manager?.name || '').trim() || (manager?.email || '').trim();
  const action = value === 'cancellation requested' ? 'Cancellation pending with' : 'Pending approval by';
  return who ? `${action} ${who}` : `${action} your manager or HR`;
};

/**
 * The workflow stage of a leave request, as one word for display beside a name.
 *
 * One definition so the calendar cell, the day popup and any future surface cannot disagree
 * about what a request's stage is called — the raw `status` string was previously printed
 * directly in some places and inferred from a CSS class (`is-approved` / `is-pending`) in
 * others, which is how a cell could style a request one way and the popup label it another.
 *
 * The stage is never abbreviated. A `compact` mode used to shorten "Cancellation Requested" to
 * "Cancelling" so it fit a narrow day cell, with the full wording only in the hover title —
 * which put the one stage people most need to read clearly behind a hover, on the surface where
 * they are deciding whether somebody is actually away. The cell now wraps to fit the word
 * instead of the word being cut to fit the cell.
 */
export const leaveStageLabel = (status: string): string => String(status || '').trim();
